import React, { useContext, useState } from 'react';
import { Stepper, Step, StepLabel, Box, Typography, Grid } from '@mui/material';
import ProjectBasics from './ProjectBasics';
import KickoffActions from './KickoffActions';
import ProjectDetails from './ProjectDetails';
import ReviewAndSend from './ReviewAndSend';
import { useReactiveVar, useMutation } from '@apollo/client/react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { KickOffContext } from '@/Contexts/KickOff';
import { openKickOffDocumentDrawer } from '@/graphql/reactiveVars';
import { CompleteKickOffDocument } from '@/graphql/generated/graphql';
import useDirectUpload from '@/hooks/useDirectUpload';
import { displayNotification } from '@/utils/notifications';
import { objectLength } from '@/utils/helperFunctions';
import OverwatchModal from '@/components/OverwatchModal';

interface CreateModalProps {
  minimized?: boolean;
  handleClose: () => void;
  handleMinimize?: (label: string) => void;
}
const steps = [
  {
    key: "kickoff-create-stepper-project-basics",
    label: "Project Basics",
    content: <ProjectBasics />
  },
  {
    key: "kickoff-create-stepper-actions-and-notes",
    label: "Actions & Notes",
    content: <KickoffActions />
  },
  {
    key: "kickoff-create-stepper-project-details",
    label: "Project Details",
    content: <ProjectDetails />
  },
  {
    key: "kickoff-create-stepper-review-and-send",
    label: "Review & Send",
    content: <ReviewAndSend />
  },
]

const CreateModal: React.FC<CreateModalProps> = ({ handleClose, handleMinimize = () => { }, minimized = false }) => {
  const [activeStep, setActiveStep] = useState(0);
  const { kickoffData, SaveAndNext, sharedWithEmails, generatedPDF } = useContext(KickOffContext);
  const openDocumentDrawer = useReactiveVar(openKickOffDocumentDrawer);
  const { error: uploadError, uploadFile } = useDirectUpload();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [completeKickOff] = useMutation(CompleteKickOffDocument, {
    refetchQueries: ["getProjectKickOffs", "getProjects"],
    onCompleted: () => {
      displayNotification({
        type: "success",
        text: "Kick-Off completed successfully",
      });
      handleClose();
    },
    onError: (error) => {
      setIsLoading(false);
      displayNotification({ type: "error", text: error.message });
    },
  });

  const primaryAction = () => {
    openKickOffDocumentDrawer(false);

    if (activeStep < objectLength(steps) - 1) {
      SaveAndNext(activeStep)
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    } else {
      if (!generatedPDF) return;
      if (!kickoffData?.id) {
        displayNotification({ type: "error", text: "Kick-off must be saved before finalising" });
        return;
      }
      setIsLoading(true);

      const pdfFile = new File(
        [generatedPDF],
        `${kickoffData.title || 'kickoff'}.pdf`,
        { type: 'application/pdf' }
      );

      uploadFile(pdfFile, "kick-off").then((response) => {
        if (!response?.signedBlobId) {
          setIsLoading(false);
          displayNotification({ type: "error", text: uploadError || "Failed to upload PDF" });
          return;
        }

        completeKickOff({
          variables: {
            kickOffId: kickoffData.id as string,
            emails: sharedWithEmails || [],
            fileUrl: response.previewUrl,
            signedId: response.signedBlobId
          }
        });
      });
    }
  }

  const handleStepClick = (index: number) => {
    openKickOffDocumentDrawer(false);

    SaveAndNext(activeStep);
    setActiveStep(index);
  };

  return (
    <OverwatchModal
      title="Project Kick-Off"
      subtitle={kickoffData ? "(Draft)" : ''}
      handleClose={() => {
        openKickOffDocumentDrawer(false);
        handleClose();
      }}
      handleMinimize={() => handleMinimize(kickoffData?.title || 'New KickOff')}
      primaryButton={<></>}
      secondaryButton={<></>}
      tertiaryButtonText={(isLoading) ? "Loading..." : activeStep === objectLength(steps) - 1 ? "Finish" : "Save & Next"}
      tertiaryButtonAction={primaryAction}
      size={openDocumentDrawer ? "xl" : "md"}
      disableEscape
      minimized={minimized}
      fullScreen={true}
      contentSx={{ zIndex: 999999 }}
    >
      <Grid container size={12} sx={{ px: 0, py: 0, borderRadius: 2 }} data-testid="kick-off-container">
        <Grid size={1.5} sx={{ borderRight: '1px solid #f0f0f0', pr: 2 }} data-testid="steps">
          <Stepper activeStep={activeStep} alternativeLabel orientation="vertical" sx={{ alignItems: 'center' }} connector={<></>}>
            {steps.map((step, index) => (
              <Step
                key={step.key}
                onClick={() => handleStepClick(index)}
                style={{ cursor: 'pointer', marginBottom: 20 }}
                data-testid="kick-off-step"
              >
                <StepLabel slots={{ stepIcon: CustomStepIcon }}>
                  <Box>
                    <Typography sx={{ fontWeight: activeStep === index ? 'bold' : 'normal' }}>
                      {step.label}
                    </Typography>
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Grid>
        <Grid size='grow' sx={{ pl: 6 }} data-testid="step-content">
          {steps[activeStep].content}
        </Grid>
      </Grid>
    </OverwatchModal>
  );
}

export default CreateModal;

interface CustomStepIconProps {
  active: boolean,
  completed: boolean,
  icon: React.ReactElement
}
const CustomStepIcon: React.FC<CustomStepIconProps> = (props) => {
  const { active, completed, icon } = props;

  if (completed) {
    return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28 }} />;
  }

  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        backgroundColor: active ? '#6200ee' : 'transparent',
        border: active ? 'none' : '1px solid #ccc',
        color: active ? '#fff' : '#888',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: 'bold',
      }}
    >
      {icon}
    </Box>
  );
};
